import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Drawer, Input, Spin, Empty } from "antd";
import {
  SearchOutlined,
  CloseOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import {
  setSelectedCity,
  openCityModal,
  closeCityModal,
  searchCities,
  clearSearchResults,
} from "../../../store/slices/citySlice";
import { api } from "../../../services/api";
import { useTheme } from "../../../theme/ThemeProvider";
import debounce from "lodash/debounce";

import NoidaImage from "../../../assets/cities/noida.jpg";
import BhopalImage from "../../../assets/cities/bhopal.jpg";
import ChandigarhImage from "../../../assets/cities/chandigarh.jpg";
import ChennaiImage from "../../../assets/cities/chennai.webp";
import GurugramImage from "../../../assets/cities/gurugram.jpeg";
import HyderabadImage from "../../../assets/cities/hyderabad.jpg";
import JaipurImage from "../../../assets/cities/jaipur.jpg";
import KolkataImage from "../../../assets/cities/kolkata.avif";
import MumbaiImage from "../../../assets/cities/mumbai.jpg";
import NewDelhiImage from "../../../assets/cities/new-delhi.avif";
import PatnaImage from "../../../assets/cities/patna.webp";
import PuneImage from "../../../assets/cities/pune.jpg";
import BengaluruImage from "../../../assets/cities/Bangalore.jpg";

const cityImages = {
  "Gurugram/Gurgaon": GurugramImage,
  "New Delhi": NewDelhiImage,
  Noida: NoidaImage,
  Mumbai: MumbaiImage,
  Bengaluru: BengaluruImage,
  Hyderabad: HyderabadImage,
  Chennai: ChennaiImage,
  Kolkata: KolkataImage,
  Pune: PuneImage,
  Jaipur: JaipurImage,
  Bhopal: BhopalImage,
  Patna: PatnaImage,
  Chandigarh: ChandigarhImage,
};

const CitySelectorModal = () => {
  const dispatch = useDispatch();
  const {
    selectedCity,
    cityModalVisible,
    searchResults,
    searching,
    searchError,
  } = useSelector((state) => state.city);

  const [searchTerm, setSearchTerm] = useState("");
  const [topCities, setTopCities] = useState([]);
  const [topCitiesLoading, setTopCitiesLoading] = useState(false);
  const isMobile = useMediaQuery("(max-width: 768px)");
  const { isDarkMode } = useTheme();

  useEffect(() => {
    if (!selectedCity?.id) {
      dispatch(openCityModal());
    }
  }, [selectedCity?.id, dispatch]);

  useEffect(() => {
    if (!cityModalVisible) {
      setSearchTerm("");
      dispatch(clearSearchResults());
    }
  }, [cityModalVisible, dispatch]);

  useEffect(() => {
    if (!cityModalVisible || topCities.length > 0) {
      return;
    }

    const loadTopCities = async () => {
      setTopCitiesLoading(true);

      try {
        const response = await api.getTopCities();
        const cities = response?.data || response;
        setTopCities(Array.isArray(cities) ? cities : []);
      } catch (error) {
        console.error("Failed to load top cities:", error);
        setTopCities([]);
      } finally {
        setTopCitiesLoading(false);
      }
    };

    loadTopCities();
  }, [cityModalVisible, topCities.length]);

  const debouncedSearch = useCallback(
    debounce((query) => {
      if (query.trim().length >= 2) {
        dispatch(searchCities(query.trim()));
      } else {
        dispatch(clearSearchResults());
      }
    }, 300),
    [dispatch],
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (value.trim().length >= 2) {
      debouncedSearch(value);
    } else {
      dispatch(clearSearchResults());
    }
  };

  const handleCityChange = (city) => {
    dispatch(setSelectedCity(city));
    dispatch(closeCityModal());
  };

  const handleClose = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    if (!selectedCity?.id) {
      dispatch(openCityModal());
      return;
    }

    dispatch(closeCityModal());
    setSearchTerm("");
    dispatch(clearSearchResults());
  };

  const isSearching = searchTerm.trim().length >= 2;
  const displayCities = isSearching ? searchResults : topCities || [];

  const isTopCity = (cityName) => {
    return topCities?.some((city) => city.name === cityName);
  };

  const CityCard = ({ city }) => {
    const isSelected = selectedCity?.id === city.id;
    const showImage = !isSearching && isTopCity(city.name);
    const imageUrl = cityImages[city.name];

    return (
      <div
        onClick={() => handleCityChange(city)}
        className={`
          relative p-2.5 rounded-lg border cursor-pointer transition-all duration-150
          ${
            isSelected
              ? `border-emerald-500 ${isDarkMode ? "bg-emerald-900/20" : "bg-emerald-50"}`
              : `border-gray-200 dark:border-gray-700 ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-50"}`
          }
        `}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleCityChange(city);
          }
        }}
        aria-label={`Select ${city.name}`}
      >
        <div className="flex items-center gap-3">
          {showImage && (
            <div className="flex-shrink-0 w-10 h-10 overflow-hidden rounded-lg">
              <img
                src={imageUrl}
                alt={city.name}
                className="object-cover w-full h-full"
                loading="lazy"
              />
            </div>
          )}

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <div
                className={`
                font-medium text-sm truncate
                ${
                  isSelected
                    ? isDarkMode
                      ? "text-emerald-400"
                      : "text-emerald-700"
                    : isDarkMode
                      ? "text-white"
                      : "text-gray-900"
                }
              `}
              >
                {city.name}
              </div>
              {isSelected && (
                <CheckCircleOutlined className="text-emerald-500 text-sm flex-shrink-0 ml-1.5" />
              )}
            </div>
            {city.state && (
              <div className="text-xs text-gray-400 truncate dark:text-gray-500">
                {city.state.name}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const CityGrid = ({ cities }) => (
    <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
      {cities.map((city) => (
        <CityCard key={city.id} city={city} />
      ))}
    </div>
  );

  const LoadingState = ({ message }) => (
    <div className="flex flex-col items-center justify-center py-8 space-y-1.5">
      <Spin
        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
        className="text-emerald-500"
      />
      <span className="text-xs text-gray-500 dark:text-gray-400">
        {message}
      </span>
    </div>
  );

  const EmptyState = ({ message }) => (
    <div className="flex flex-col items-center justify-center py-8 space-y-1.5">
      <div className="flex items-center justify-center w-12 h-12 bg-gray-100 rounded-full dark:bg-gray-800">
        <EnvironmentOutlined className="text-lg text-gray-400 dark:text-gray-600" />
      </div>
      <p className="text-sm text-center text-gray-500 dark:text-gray-400">
        {message}
      </p>
    </div>
  );

  const ErrorState = ({ message, onRetry }) => (
    <div className="flex flex-col items-center justify-center py-8 space-y-2.5">
      <p className="text-sm text-center text-red-500 dark:text-red-400">
        {message}
      </p>
      <button
        onClick={onRetry}
        className="px-4 py-1 text-sm font-medium text-white transition-colors duration-150 rounded-lg bg-emerald-500 hover:bg-emerald-600"
      >
        Try Again
      </button>
    </div>
  );

  const CityList = () => {
    const showLoading = !isSearching && topCitiesLoading;
    const showSearching = isSearching && searching;
    const showError = isSearching && searchError && !searching;
    const showEmpty =
      !isSearching && !topCitiesLoading && displayCities.length === 0;
    const showSearchEmpty =
      isSearching && !searching && !searchError && displayCities.length === 0;

    if (showLoading) {
      return <LoadingState message="Loading cities..." />;
    }

    if (showSearching) {
      return <LoadingState message="Searching..." />;
    }

    if (showError) {
      return (
        <ErrorState
          message={searchError}
          onRetry={() => dispatch(searchCities(searchTerm.trim()))}
        />
      );
    }

    if (showEmpty) {
      return <EmptyState message="No cities available" />;
    }

    if (showSearchEmpty) {
      return <EmptyState message={`No cities found for "${searchTerm}"`} />;
    }

    if (displayCities.length > 0) {
      return (
        <>
          <CityGrid cities={displayCities} />
        </>
      );
    }

    return null;
  };

  const ModalContent = () => (
    <div className="flex flex-col h-full">
      <div className="flex-shrink-0 pb-3">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          {isSearching ? "Search Results" : "Choose Your City"}
        </h2>
        {!isSearching && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Select your location for the best deals
          </p>
        )}
      </div>

      <div className="flex-shrink-0 sticky top-0 z-10 bg-white dark:bg-gray-900 pb-2.5">
        <Input
          placeholder="Search for your city..."
          prefix={<SearchOutlined className="text-sm text-gray-400" />}
          value={searchTerm}
          onChange={handleSearchChange}
          allowClear
          className="text-sm transition-colors duration-150 border-gray-200 rounded-lg h-9 dark:border-gray-700 hover:border-emerald-400 focus:border-emerald-500 dark:bg-gray-800 dark:text-white"
          autoFocus
          onKeyDown={(e) => e.stopPropagation()}
        />
      </div>

      <div
        className="flex-1 overflow-y-auto min-h-[160px] max-h-[380px] -mr-1 pr-1 
        scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 
        scrollbar-track-transparent"
      >
        {!isSearching && topCities?.length > 0 && !topCitiesLoading && (
          <div className="mb-1.5">
            <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">
              Popular Cities
            </p>
          </div>
        )}
        <CityList />
      </div>
    </div>
  );

  // Mobile Drawer
  if (isMobile) {
    return (
      <Drawer
        title={null}
        placement="bottom"
        onClose={handleClose}
        open={cityModalVisible}
        height="85%"
        closable={false}
        styles={{
          header: {
            display: "none",
          },
          body: {
            background: isDarkMode ? "#111827" : "#ffffff",
            padding: "12px 14px 16px",
            borderRadius: "16px 16px 0 0",
            position: "relative",
          },
          wrapper: {
            background: "rgba(0,0,0,0.5)",
          },
        }}
      >
        {/* Mobile Close Button */}
        <div className="relative">
          <button
            onClick={handleClose}
            className="absolute top-0 right-0 z-50 flex items-center justify-center w-8 h-8 transition-colors duration-150 bg-gray-100 rounded-full dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700"
            aria-label="Close"
            type="button"
          >
            <CloseOutlined className="text-xs text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Drag Handle */}
        <div className="flex justify-center mb-2 -mt-1">
          <div className="w-10 h-0.5 rounded-full bg-gray-300 dark:bg-gray-600" />
        </div>

        <ModalContent />
      </Drawer>
    );
  }

  // Desktop Modal
  return (
    <Modal
      title={null}
      open={cityModalVisible}
      onCancel={handleClose}
      footer={null}
      width={620}
      centered={false}
      style={{ top: 40 }}
      closable={false}
      styles={{
        body: {
          background: isDarkMode ? "#1f2937" : "#ffffff",
          padding: "18px 22px 22px",
          borderRadius: "14px",
          position: "relative",
        },
        wrapper: {
          background: "rgba(0,0,0,0.4)",
          backdropFilter: "blur(4px)",
        },
        mask: {
          background: "transparent",
        },
        content: {
          position: "relative",
          overflow: "visible",
        },
      }}
    >
      {/* Desktop Close Button - Inside Modal children */}
      <div className="relative">
        <button
          onClick={handleClose}
          className="absolute top-0 right-0 z-50 flex items-center justify-center w-8 h-8 -mt-2 -mr-2 transition-colors duration-150 bg-white rounded-full shadow-sm dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
          aria-label="Close"
          type="button"
        >
          <CloseOutlined className="text-xs text-gray-600 dark:text-gray-400" />
        </button>
        <ModalContent />
      </div>
    </Modal>
  );
};

export default CitySelectorModal;
